import { CommonModule, DatePipe } from "@angular/common";
import { Component, computed, DestroyRef, EventEmitter, inject, input, Output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ButtonModule } from "primeng/button";
import { getRelativeTimeMessage } from "../../../../shared/utils/formatting-helper";
import SyncStatusService, { SyncStatus } from "../../../../core/sync/sync-status-service";
import SyncManager from "../../../../core/sync/sync-manager";
import { ProgressBar } from "primeng/progressbar";


enum SyncStatusState {
  Offline,
  InProgress,
  Complete,
  Failed,
  Incomplete
}

interface SyncStatusSheetInfo {
  iconClass: string;
  title: string;
  message: string;
  actionButtonText: string;
}

const syncStatusSheetInfoMap: Record<SyncStatusState, SyncStatusSheetInfo> = {
  [SyncStatusState.Offline]: {
    iconClass: "icon-offline",
    title: "You're offline",
    message: "To sync, reconnect to the internet and open the app.",
    actionButtonText: "Close"
  },
  [SyncStatusState.InProgress]: {
    iconClass: "icon-in-progress",
    title: "Sync in progress",
    message: "Keep this app open until sync completes. If you close the app, syncing will pause.",
    actionButtonText: "Syncing"
  },
  [SyncStatusState.Complete]: {
    iconClass: "icon-complete",
    title: "All work is synced",
    message: "Your device is connected and up to date.",
    actionButtonText: "Sync Now",
  },
  [SyncStatusState.Failed]: {
    iconClass: "icon-failed breathe",
    title: "Sync didn't complete",
    message: "Check your connection and try again. Keep your phone on and the app open while syncing. If the issue continues, call Pulte IT for help.",
    actionButtonText: "Retry Sync",
  },
  [SyncStatusState.Incomplete]: {
    iconClass: "icon-incomplete",
    title: "Pending Items",
    message: "Most of your work is synced, but a few items are still saved only on this device.",
    actionButtonText: "Retry Pending Items",
  }
};

@Component({
  selector: "sync-status-drawer",
  standalone: true,
  imports: [CommonModule, ButtonModule, ProgressBar],
  providers: [DatePipe],
  templateUrl: "./sync-status-drawer.component.html",
  styleUrl: "./sync-status-drawer.component.scss"
})
export class SyncStatusSheetComponent {
  onlineStatus = input<boolean>();
  syncStatus = input<SyncStatus>();
  lastSynced = input<Date | null>();
  @Output() onSyncSheetClose = new EventEmitter<void>();

  syncStatusStateEnum = SyncStatusState;
  syncProgress: number = 50;
  private readonly destroyRef = inject(DestroyRef);

  constructor(private datePipe: DatePipe, private syncManager: SyncManager, private syncStatusService: SyncStatusService) { }

  ngOnInit(): void {
    this.syncStatusService.syncProgressChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async progress => {
      this.syncProgress = progress;
    });
  }

  getSyncStatusState = computed((): SyncStatusState => {
    if (this.onlineStatus() === false) {
      return SyncStatusState.Offline;
    } else if (this.syncStatus() === SyncStatus.InProgress) {
      return SyncStatusState.InProgress;
    } else if (this.syncStatus() === SyncStatus.Complete) {
      return SyncStatusState.Complete;
    } else if (this.syncStatus() === SyncStatus.Failed) {
      return SyncStatusState.Failed;
    } else if (this.syncStatus() === SyncStatus.Incomplete) {
      return SyncStatusState.Incomplete;
    }
    return SyncStatusState.Complete;
  });

  getSyncStatusInfo = computed(() => {
    const statusState = this.getSyncStatusState();
    return syncStatusSheetInfoMap[statusState];
  });

  getLastSyncedMessage = computed(() => {
    const statusState = this.getSyncStatusState();
    const lastSynced = this.lastSynced();
    const formattedLastSyncedDate = this.datePipe.transform(lastSynced, "EEEE',' MMMM d 'at' h:mm a");
    const formattedRelativeTime = lastSynced ? getRelativeTimeMessage(lastSynced) : "";

    if (statusState === SyncStatusState.Failed) {
      return `Your work is saved - last synced ${formattedLastSyncedDate}, ${formattedRelativeTime}.`;
    } else if (statusState === SyncStatusState.Incomplete) {
      return `Saved to this device – last synced ${formattedLastSyncedDate}, ${formattedRelativeTime}.`;
    }
    return `Last synced ${formattedLastSyncedDate}, ${formattedRelativeTime}`;
  });

  onSyncStatusAction(): void {
    this.onSyncSheetClose.emit();
    this.syncStatusService.setSyncStatus(SyncStatus.InProgress);

    const isForceSync = this.getSyncStatusState() !== SyncStatusState.Incomplete;
    const sendJobsIndividually = true;
    this.syncManager.manuallyTriggerSync(isForceSync, sendJobsIndividually);
  }
}